import { mockCustomers } from '@shared/data/mockCustomers';
import type { Customer } from '@shared/types/customers';
import type { EntityListResponse } from '@shared/types/entity.types';
import { MockService } from './mock-service';
import type { StandardEntityParams } from './base-service';
import { type CustomerQueryFilters } from '@shared/utils/query/configs';
import { SEARCH_FIELDS, FACET_FIELDS, PAGINATION } from './constants/service.constants';
import { StatusRegistry } from '@shared/lib/filters/status-registry';

interface GetCustomersParams extends StandardEntityParams {
  filters?: CustomerQueryFilters;
}

class CustomersService extends MockService<Customer, GetCustomersParams> {
  protected mockData = mockCustomers;

  constructor() {
    super({
      entityName: 'customer',
    });
  }

  protected readonly searchFields = SEARCH_FIELDS.CUSTOMERS;
  protected readonly facetFields = FACET_FIELDS.CUSTOMERS;

  protected getCustomFilters(): Record<string, (items: Customer[], value: unknown) => Customer[]> {
    return {
      verified: (items, value) => {
        if (value !== undefined) {
          return items.filter((customer: Customer) => customer.verified === value);
        }
        return items;
      }
    };
  }

  protected getStatusMappings(): Record<string, readonly string[]> {
    return StatusRegistry.getMappings('customer') as Record<string, string[]>;
  }

  private transformCustomer(customer: Customer): Customer {
    return {
      ...customer,
      created: customer.created instanceof Date ? customer.created : new Date(customer.created || new Date()),
      lastPayment: customer.lastPayment instanceof Date ? customer.lastPayment : customer.lastPayment ? new Date(customer.lastPayment) : undefined,
      lastActivity: customer.lastActivity instanceof Date ? customer.lastActivity : customer.lastActivity ? new Date(customer.lastActivity) : undefined,
      restricted: customer.restricted instanceof Date ? customer.restricted : customer.restricted ? new Date(customer.restricted) : undefined,
      terminated: customer.terminated instanceof Date ? customer.terminated : customer.terminated ? new Date(customer.terminated) : undefined,
    };
  }

  async getCustomers(params: GetCustomersParams = {}): Promise<EntityListResponse<Customer>> {
    this.mockData = this.mockData.map(customer => this.transformCustomer(customer));
    
    const pageSize = params.pageSize || PAGINATION.DEFAULT_PAGE_SIZE;
    const currentPage = (params.page || PAGINATION.DEFAULT_PAGE) + 1; // MockService expects 1-based page numbers
    
    const result = await this.getEntitiesForNavigation(currentPage, {
      pageSize,
      statusFilter: params.status,
      query: params.query,
      ...params.filters
    });
    
    return {
      results: result.data,
      totalElements: result.total,
      totalPages: Math.ceil(result.total / pageSize),
      pageNumber: params.page || PAGINATION.DEFAULT_PAGE,
      pageSize,
      facets: result.facets
    };
  }

  async getCustomerById(customerId: string): Promise<Customer> {
    const customer = await this.getEntityById(customerId);
    return this.transformCustomer(customer);
  }

  async updateCustomer(customerId: string, customerData: Partial<Customer>): Promise<Customer> {
    const index = this.mockData.findIndex(c => c.id === customerId)
    if (index === -1) {
      throw this.createServiceError('update', new Error(`Customer ${customerId} not found`))
    }
    this.mockData[index] = { ...this.mockData[index], ...customerData }
    return this.transformCustomer(this.mockData[index])
  }

  async createCustomer(customerData: Partial<Customer>): Promise<Customer> {
    // Generate a new customer ID
    const newId = `cust_${crypto.randomUUID()}`;

    // Create the new customer with default values
    const newCustomer: Customer = {
      id: newId,
      name: customerData.name || '',
      email: customerData.email || '',
      phone: customerData.phone,
      location: customerData.location,
      status: 'active',
      created: new Date(),
      totalSpent: 0,
      paymentCount: 0,
      verified: false,
      type: customerData.type || 'individual',
      riskLevel: 'low',
      riskScore: 0,
      refunds: 0,
      refundCount: 0,
      disputeLosses: 0,
      disputeCount: 0,
      delinquent: false,
      ...customerData,
    };

    // Add to mock data
    this.mockData = [newCustomer, ...this.mockData];

    return this.transformCustomer(newCustomer);
  }

}

export const customersService = new CustomersService();